<div class="clock-in-out-container">
  <div class="clock-header">
    <mat-card>
      <mat-card-content class="time-display">
        <div class="current-time">
          <mat-icon>schedule</mat-icon>
          <span class="time">{{ getCurrentTime() }}</span>
        </div>
        <div class="current-date">
          <mat-icon>calendar_today</mat-icon>
          <span class="date">{{ getCurrentDate() }}</span>
        </div>
      </mat-card-content>
    </mat-card>
  </div>

  <div class="clock-content">
    <mat-card>
      <mat-card-header>
        <mat-card-title>
          <mat-icon>fingerprint</mat-icon>
          Fingerprint Clock In/Out
        </mat-card-title>
        <mat-card-subtitle>Scan your fingerprint to clock in or out</mat-card-subtitle>
      </mat-card-header>

      <mat-card-content>
        <form [formGroup]="clockForm" (ngSubmit)="onSubmit()" class="clock-form">
          <div class="form-section">
            <div class="fingerprint-section">
              <div class="fingerprint-icon">
                <mat-icon>fingerprint</mat-icon>
              </div>
              
              <mat-form-field appearance="outline" class="fingerprint-field">
                <mat-label>Fingerprint ID</mat-label>
                <input 
                  matInput 
                  formControlName="fingerprintId" 
                  placeholder="Enter or scan fingerprint ID"
                  id="fingerprintId"
                  autocomplete="off">
                <mat-icon matSuffix>scanner</mat-icon>
                <mat-error *ngIf="clockForm.get('fingerprintId')?.hasError('required')">
                  Fingerprint ID is required
                </mat-error>
                <mat-error *ngIf="clockForm.get('fingerprintId')?.hasError('minlength')">
                  Fingerprint ID must be at least 3 characters
                </mat-error>
              </mat-form-field>
            </div>

            <div class="optional-fields">
              <mat-form-field appearance="outline">
                <mat-label>Device ID</mat-label>
                <input matInput formControlName="deviceId" [readonly]="true" placeholder="Auto-populated">
                <mat-icon matSuffix>devices</mat-icon>
                <mat-hint>Auto-detected from your IP address</mat-hint>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Location</mat-label>
                <input matInput formControlName="location" [readonly]="true" placeholder="Auto-populated">
                <mat-icon matSuffix>location_on</mat-icon>
                <mat-hint>Auto-detected from your device</mat-hint>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Notes (Optional)</mat-label>
                <textarea matInput formControlName="notes" placeholder="Enter any notes (e.g., late arrival, early departure)" rows="2"></textarea>
                <mat-icon matSuffix>note</mat-icon>
                <mat-hint>Add notes for late arrival, early departure, or other reasons</mat-hint>
              </mat-form-field>
            </div>
          </div>

          <div class="camera-section">
            <div class="camera-container">
              <div *ngIf="!photoDataUrl && cameraActive" class="video-container">
                <video #videoRef id="clockinout-video" width="320" height="240" autoplay></video>
                <div class="camera-overlay">
                  <mat-icon>camera_alt</mat-icon>
                  <span>Position your face in the frame</span>
                </div>
              </div>
              <div *ngIf="photoDataUrl" class="photo-container">
                <img [src]="photoDataUrl" width="320" height="240" alt="Captured Photo" />
                <div class="photo-overlay">
                  <mat-icon>check_circle</mat-icon>
                  <span>Photo captured successfully</span>
                </div>
              </div>
              <div *ngIf="!cameraActive" class="camera-disabled">
                <mat-icon>camera_off</mat-icon>
                <span>Camera not available</span>
              </div>
            </div>
            
            <div class="camera-actions">
              <button 
                *ngIf="!photoDataUrl && cameraActive"
                mat-raised-button 
                color="primary" 
                (click)="capturePhoto()" 
                [disabled]="!videoReady"
                class="capture-btn">
                <mat-icon>camera_alt</mat-icon>
                Capture Photo
              </button>
              <button 
                *ngIf="photoDataUrl"
                mat-stroked-button 
                color="accent" 
                (click)="resetPhoto()"
                class="retake-btn">
                <mat-icon>refresh</mat-icon>
                Retake Photo
              </button>
            </div>
          </div>

          <div class="submit-section">
            <button 
              mat-raised-button 
              color="primary" 
              type="submit" 
              [disabled]="clockForm.invalid || loading || !photoDataUrl"
              class="submit-button">
              <mat-icon *ngIf="!loading">login</mat-icon>
              <mat-spinner *ngIf="loading" diameter="20"></mat-spinner>
              <span>{{ loading ? 'Processing...' : 'Clock In/Out' }}</span>
            </button>
          </div>
        </form>
      </mat-card-content>
    </mat-card>
  </div>

  <!-- Last Action Display -->
  <div class="last-action" *ngIf="lastAction">
    <mat-card>
      <mat-card-header>
        <mat-card-title>
          <mat-icon>check_circle</mat-icon>
          Last Action
        </mat-card-title>
      </mat-card-header>
      <mat-card-content>
        <div class="action-details">
          <div class="action-item">
            <strong>Fingerprint ID:</strong> {{ lastAction.fingerprintId }}
          </div>
          <div class="action-item">
            <strong>Action:</strong> 
            <span class="action-badge" [class.clock-in]="lastAction.message.includes('in')" [class.clock-out]="lastAction.message.includes('out')">
              {{ lastAction.message.includes('in') ? 'CLOCK IN' : 'CLOCK OUT' }}
            </span>
          </div>
          <div class="action-item">
            <strong>Time:</strong> {{ lastAction.timestamp | date:'medium' }}
          </div>
          <div class="action-item" *ngIf="lastAction.id">
            <strong>Record ID:</strong> {{ lastAction.id }}
          </div>
        </div>
      </mat-card-content>
    </mat-card>
  </div>

  <!-- Instructions -->
  <div class="instructions">
    <mat-card>
      <mat-card-header>
        <mat-card-title>
          <mat-icon>help</mat-icon>
          Instructions
        </mat-card-title>
      </mat-card-header>
      <mat-card-content>
        <div class="instruction-list">
          <div class="instruction-item">
            <mat-icon>touch_app</mat-icon>
            <span>Place your finger on the fingerprint scanner or enter your fingerprint ID manually</span>
          </div>
          <div class="instruction-item">
            <mat-icon>camera_alt</mat-icon>
            <span>Position your face in the camera frame and capture a photo for verification</span>
          </div>
          <div class="instruction-item">
            <mat-icon>schedule</mat-icon>
            <span>The system will automatically determine if you're clocking in or out based on your last action</span>
          </div>
          <div class="instruction-item">
            <mat-icon>notifications</mat-icon>
            <span>You'll receive a confirmation message after successful clock in/out</span>
          </div>
        </div>
      </mat-card-content>
    </mat-card>
  </div>
</div> 